import { loadStripe } from "@stripe/stripe-js";
import React from "react";
import date from "./dateAdapter.js";
import * as http from "./http.ts";
import * as utils from "./utils.jsx";

import CountdownTimer from "./CountdownTimer.jsx";

function PaymentForm({
  wp,
  user,
  registration,
  useMemberPricing,
  scholarship_amount_awarded,
  stripePublishableKey,
  stripePaymentIntentClientSecret,
  enableTimer = true,
  updateRegistration,
  setStep,
}) {
  const [state, setState] = React.useState({
    timed_out: false,
    submitting: false,
    error: "",
    errors: {},
  });

  const stripeContainerRef = React.useRef(null);
  const stripeRef = React.useRef(null);
  const elementsRef = React.useRef(null);
  const paymentElementRef = React.useRef(null);

  React.useEffect(() => {
    let mounted = true;

    loadStripe(stripePublishableKey).then((stripe) => {
      if (!mounted || !stripe) return;

      stripeRef.current = stripe;
      elementsRef.current = stripe.elements({
        clientSecret: stripePaymentIntentClientSecret,
      });
      paymentElementRef.current = elementsRef.current.create("payment");
      paymentElementRef.current.mount(stripeContainerRef.current);
    });

    return () => {
      mounted = false;
      paymentElementRef.current?.unmount();
      paymentElementRef.current = null;
      elementsRef.current = null;
      stripeRef.current = null;
    };
  }, [stripePaymentIntentClientSecret, stripePublishableKey]);

  function getLineItems() {
    const reg = registration;

    const usingMemberPricing = useMemberPricing || utils.isMember(user);
    var price;
    var status = usingMemberPricing ? "Member" : "";
    if (usingMemberPricing) {
      price = wp.member_fee;
      status = "Member";
    } else {
      price = wp.non_member_fee;
    }
    if (scholarship_amount_awarded) {
      price = Math.max(0, price - scholarship_amount_awarded);
      status += "; $" + scholarship_amount_awarded.toFixed(2) + " Scholarship";
    }

    var wpName = wp.name;
    if (wp.start_date_time) {
      wpName += ", " + date.format(date.toDate(wp.start_date_time), "MMM D");
    }

    var items = [
      {
        id: 1,
        wpName: wpName,
        name: reg.firstName + " " + reg.lastName,
        price: price,
        status: status,
      },
    ];

    var total = price;
    (reg.others || []).forEach(function (other, i) {
      var other_is_member = other.is_current_member;
      var in_member_hh = usingMemberPricing && other.declaredInHH;
      var already_registered = other.errors == "AlreadyRegistered";
      var other_price;
      var status = "";
      var otherWp = other.wp || wp;
      var otherWpName = otherWp.name;
      if (otherWp.start_date_time) {
        otherWpName +=
          ", " + date.format(date.toDate(otherWp.start_date_time), "MMM D");
      }
      if (already_registered) {
        other_price = 0;
        status = "Already Registered";
      } else if (other_is_member || in_member_hh) {
        other_price = otherWp.member_fee;
        status = other_is_member ? "Member" : "In Household";
      } else {
        other_price = otherWp.non_member_fee;
      }
      if (other.scholarship_amount_awarded) {
        other_price = Math.max(
          0,
          other_price - other.scholarship_amount_awarded,
        );
        status +=
          "; $" + other.scholarship_amount_awarded.toFixed(2) + " Scholarship";
      }
      items.push({
        id: i + 2,
        wpName: otherWpName,
        name: other.firstName + " " + other.lastName,
        status: status,
        price: other_price,
      });
      total += other_price;
    });
    return [items, total];
  }

  function handleSubmit() {
    // show loading indicator
    setState((currentState) => ({
      ...currentState,
      submitting: true,
      error: "",
    }));

    // finalize payment with Stripe
    stripeRef.current
      .confirmPayment({
        elements: elementsRef.current,
        confirmParams: {
          return_url: window.base_url + "workparty/" + wp._id,
        },
        redirect: "if_required",
      })
      .then((result) => {
        if (result.error) {
          // if there was an error, show the user
          setState((currentState) => ({
            ...currentState,
            submitting: false,
            error: result.error.message,
          }));
        } else {
          // record payment in Salesforce
          recordPayment(result);
        }
      });
  }

  function recordPayment(result) {
    // Call backend to record payment in Salesforce
    const action_url =
      window.base_url + "workparty/" + wp._id + "/stripe-payment-succeeded";
    http
      .postJson(action_url, {
        payment_intent_id: result.paymentIntent.id,
      })
      .then(() => {
        // Successfully recorded; show the confirmation page
        setState((currentState) => ({ ...currentState, submitting: false }));
        updateRegistration({
          ...registration,
          status: "Registered",
        });
        setStep("confirm");
      })
      .catch(() => {
        // Failed to record payment in Salesforce.
        // Not much we can do except direct the user to contact WTA.
        setState((currentState) => ({
          ...currentState,
          submitting: false,
          error:
            "Your payment was processed, but could not be recorded in WTA's system. " +
            "Please contact WTA to confirm your registration.",
        }));
      });
  }

  function cancelRegs() {
    setState((currentState) => ({ ...currentState, timed_out: true }));
    var action_url = window.base_url + "workparty/" + wp._id + "/cancel";
    const reg = registration;
    var message = "";

    function cancel_reg(reg_id) {
      var data = reg_id ? { cancelReg: reg_id } : {};
      data.reg_status = "Payment Not Provided";
      http.postJson(action_url, data).catch((error) => {
        // Combine error messages
        message += "\n\n" + error.state.error;
        setState((currentState) => ({
          ...currentState,
          ...error.state,
          error: message,
        }));
      });
    }

    for (var i = 0; i < (reg.others || []).length; i++) {
      var reg_id = reg.others[i].id;
      if (reg_id) {
        cancel_reg(reg.others[i].id);
      }
    }
    // Wait to cancel primary registration, to avoid permission race condition
    setTimeout(cancel_reg, 200);
  }

  var [items, total] = getLineItems();

  return (
    <div>
      <div className="ui progress">
        <div className="bar">1. Register</div>
        <div className="bar on">2. Payment</div>
        <div className="bar">3. Confirm</div>
      </div>
      {enableTimer ? (
        <CountdownTimer timeout={300} onTimeout={cancelRegs} />
      ) : (
        ""
      )}
      <section className="main">
        <article className="wrapper">
          <section className="utility">
            <div className="row">
              <div className="col six">
                <div
                  id="stripe-payment-container"
                  ref={stripeContainerRef}
                ></div>
                <br />
              </div>
              <div className="col six">
                <h2>Summary</h2>
                <table>
                  <tbody>
                    {items.map((item, i) => (
                      <tr key={i}>
                        <td>
                          <div>
                            <em>{item.name}</em>{" "}
                            {item.status ? (
                              <span className="OtherStatus">
                                ({item.status})
                              </span>
                            ) : (
                              ""
                            )}
                          </div>
                          {item.wpName}
                        </td>
                        <td>${item.price.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h2>Total Due</h2>
                <hr />
                <p>
                  <strong>${total.toFixed(2)}</strong>
                </p>
                <div className="ui">
                  <input
                    className="button"
                    type="button"
                    value={
                      state.submitting ? "Processing payment..." : "Pay Now!"
                    }
                    disabled={state.submitting || state.timed_out}
                    onClick={handleSubmit}
                  />
                </div>
                {state.error ? (
                  <div className="validation-error">
                    <div className="container">{state.error}</div>
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        </article>
      </section>
    </div>
  );
}

export default PaymentForm;
